import React, { createContext, useState, useContext, ReactNode, useRef, useEffect } from 'react';
import Alert, { AlertColor } from '@mui/material/Alert';
import Stack from '@mui/material/Stack';

// Define the toast context type
interface ToastContextType {
  showToast: (message: string, severity: AlertColor, duration?: number) => void;
  hideToast: () => void;
}

// Create the context with a default value
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Define props for the ToastProvider component
interface ToastProviderProps {
  children: ReactNode;
}

// Toast message interface
interface ToastMessage {
  id: number;
  message: string;
  severity: AlertColor;
  duration: number;
}

// Create the ToastProvider component
export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextIdRef = useRef(0);
  const timeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  // Function to show a toast
  const showToast = (message: string, severity: AlertColor = 'info', duration = 6000) => {
    const id = nextIdRef.current++;
    setToasts((prev) => [...prev, { id, message, severity, duration }]);
    
    const timeoutId = setTimeout(() => {
      setToasts((prev) => prev.filter(toast => toast.id !== id));
      timeoutsRef.current.delete(id);
    }, duration);
    
    timeoutsRef.current.set(id, timeoutId);
  };

  // Function to hide the toast
  const hideToast = () => {
    setToasts([]);
  };

  // Handle close event
  const handleClose = (id: number) => {
    const timeoutId = timeoutsRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsRef.current.delete(id);
    }
    setToasts((prev) => prev.filter(toast => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Stack
        spacing={1}
        sx={{
          position: 'fixed',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          minWidth: 300,
          maxWidth: 500,
        }}
      >
        {toasts.map((toast) => (
          <Alert
            key={toast.id}
            onClose={() => handleClose(toast.id)}
            severity={toast.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {toast.message}
          </Alert>
        ))}
      </Stack>
    </ToastContext.Provider>
  );
};

// Custom hook to use the toast context
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};